import { useContext, useEffect, useState } from 'react';

import AddTask from '@/components/AddTask';
import PageHeader from '@/components/PageHeader';
import Pagination from '@/components/Pagination';
import TaskItem from '@/components/TaskItem';
import defaultTaskFilters from '@/constants/defaultTaskFilters';
import { STICKY_CHROME_CLASS } from '@/constants/layout';
import { defaultTaskSortOption } from '@/constants/taskSortOptions';
import TaskFilterForm from '@/forms/TaskFilterForm';
import type { TaskFilters } from '@/forms/TaskFilterForm/types';
import useAPI from '@/hooks/useAPI';
import usePagination from '@/hooks/usePagination';
import useQueryString from '@/hooks/useQueryString';
import PersonContext from '@/pages/Person/PersonContext';
import WorkRepository from '@/repositories/WorkRepository';
import type { ItemParams } from '@/types/item';
import Container from '@/ui/components/Container';
import { OptionType } from '@/ui/components/Form/Select/types';
import Heading from '@/ui/components/Heading/Heading';
import LoadingSkeleton from '@/ui/components/LoadingSkeleton';
import formatPersonName from '@/utils/formatPersonName';
import getInitialFilters from '@/utils/getInitialFilters';

const PersonTaskList = () => {
  const { person } = useContext(PersonContext);
  const [totalPages, setTotalPages] = useState<number>(0);

  const {
    params: queryParams,
    setParams: setQueryParams,
    hasQueryParams
  } = useQueryString<Partial<ItemParams>>();
  const [params, setParams] = useState<ItemParams>(() => {
    if (hasQueryParams) {
      return { ...queryParams, page: queryParams.page || '1' } as ItemParams;
    }
    return {
      ...getInitialFilters({
        defaultFilters: {
          ...defaultTaskFilters,
          status: ['open', 'in_progress']
        }
      }),
      page: '1'
    };
  });
  const [sort, setSort] = useState<OptionType<string>>(defaultTaskSortOption);
  const workRepo = WorkRepository();

  const setFilters = (filters: Partial<TaskFilters>) => {
    return setParams({ ...filters, page: '1' });
  };

  useEffect(() => {
    setQueryParams({ ...params, ordering: sort.value });
  }, [params, sort, setQueryParams]);

  const {
    data: itemsResponse,
    isPending: isLoadingItems,
    isSuccess
  } = useAPI({
    queryKey: ['getPersonTasks', person.id, { ...params, ordering: sort.value }],
    queryFn: async () => {
      return await workRepo.getItems({
        personId: person.id,
        params: {
          ...params,
          ordering: sort.value
        }
      });
    }
  });

  useEffect(() => {
    if (isSuccess) {
      if (itemsResponse?.data?.page) {
        setParams({ ...params, page: itemsResponse.data.page.toString() });
      }

      if (itemsResponse?.data?.total_pages) {
        setTotalPages(itemsResponse.data.total_pages);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, itemsResponse]);

  const { currentPage, goToPage, nextPage, prevPage } = usePagination(
    parseInt(params.page, 10),
    totalPages
  );

  useEffect(() => {
    setParams((currentParams) => ({ ...currentParams, page: currentPage.toString() }));
  }, [currentPage]);

  return (
    <Container type="page">
      <div className={`mb-4 pt-1 z-19 bg-gray-100 ${STICKY_CHROME_CLASS.pageSection}`}>
        <PageHeader headingText="Tasks" className="sr-only" />
        <TaskFilterForm
          activeFilters={params}
          setFilters={setFilters}
          setSort={setSort}
          sort={sort}
        />
      </div>

      <Pagination
        currentPage={currentPage}
        goToPage={goToPage}
        nextPage={nextPage}
        prevPage={prevPage}
        totalPages={totalPages}>
        {isLoadingItems && <LoadingSkeleton spinnerProps={{ label: 'Loading tasks' }} />}

        {!isLoadingItems && !itemsResponse?.data?.results.length ? (
          <div className="text-center p-8">
            <Heading text={`No tasks found for ${formatPersonName({ person })}`} value={3} />

            <div className="mt-3">
              <AddTask personId={person.id} />
            </div>
          </div>
        ) : (
          <ul role="list">
            {itemsResponse?.data?.results.map((item) => (
              <li key={item.id}>
                <TaskItem item={item} />
              </li>
            ))}
          </ul>
        )}
      </Pagination>
    </Container>
  );
};

export default PersonTaskList;
